/**
 * 푸시 알림 — 앱(안드로이드)과 웹을 한자리에서(DEV-12 3).
 *
 * - 웹: 브라우저 Notification 권한만(보낼 길은 아직 없음, 기존 그대로)
 * - 앱: @capacitor/push-notifications 로 권한을 묻고 FCM 기기 토큰을 서버(POST /api/me/push-token)에 둠.
 *   알림을 누르면 data.path(서버 고정 문안의 경로)로. 로그아웃 때 이 기기 토큰을 지움
 * 알림 물음: 앱은 로그인해 들어오면 바로 한 번(D109, startNativePush). 웹 · 아직 안 물은 기기는 별채 다음 날 아침 카드 아래(시안 05 결정 19, RoomPage 의 canAskPush · requestPush).
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { api } from '../api/client.js';

/** 앱(안드로이드 껍데기) 안인지 */
export function isNativeApp() {
    return Capacitor.isNativePlatform();
}

// 앱의 기기 권한 상태(시작할 때 한 번 읽어 둠: 화면이 그리면서 바로 물어볼 수 있게)
let nativePermission = 'unknown';
let deviceToken = null;

let started = false;

const ENABLED_KEY = 'insaeng.push.enabled';

/** 이 기기에서 앱 알림을 켜 두었는가(나 → 알림 설정의 「앱 알림」, 히어로챗 /settings/notifications 와 같음). 기본 켜짐 */
export function pushEnabled() {
    try {
        return localStorage.getItem(ENABLED_KEY) !== 'false';
    } catch {
        return true;
    }
}

function setPushEnabled(enabled) {
    try {
        localStorage.setItem(ENABLED_KEY, String(enabled));
    } catch {
        // 기기에 못 적어도 이번 실행에서는 그대로 돕니다
    }
}

/** 기기 알림 권한: 'granted' · 'denied' · 'prompt' · 'prompt-with-rationale' · 앱이 아니면 'unavailable' */
export async function pushPermission() {
    if (!isNativeApp()) {
        return 'unavailable';
    }
    try {
        const { receive } = await PushNotifications.checkPermissions();
        nativePermission = receive;
        return receive;
    } catch {
        return 'unavailable';
    }
}

/**
 * 「앱 알림」 켜기: 아직 안 물었으면 묻고, 허용이면 기기를 등록합니다(registration 이 토큰을 서버에 둠).
 * @returns {Promise<string>} 그 뒤 권한 상태. 'denied' 면 화면이 기기 설정에서 켜라고 안내합니다
 */
export async function enablePush() {
    setPushEnabled(true);
    let receive = await pushPermission();
    if (receive === 'prompt' || receive === 'prompt-with-rationale') {
        ({ receive } = await PushNotifications.requestPermissions());
        nativePermission = receive;
    }
    if (receive === 'granted') {
        await PushNotifications.register();
    }
    return receive;
}

/**
 * 「앱 알림」 끄기: 이 기기 토큰을 서버에서 지우고 기기의 FCM 등록도 풉니다(나 탭의 종류별 스위치는 그대로).
 * 이번 실행에서 토큰을 못 받았어도, 등록을 풀면 서버에 남은 옛 토큰은 다음 발송 때 「없는 토큰」으로 지워집니다(notify.js)
 */
export async function disablePush() {
    setPushEnabled(false);
    if (!isNativeApp()) {
        return true;
    }
    const token = deviceToken;
    deviceToken = null;
    let removed = true;
    if (token) {
        removed = await api('/me/push-token', { method: 'DELETE', body: { token } })
            .then(() => true)
            .catch(() => false);
    }
    await PushNotifications.unregister().catch(() => {});
    return removed;
}

/** 아직 묻지 않았을 때만 true(웹은 Notification.permission, 앱은 기기 권한) */
export function canAskPush(win = typeof window === 'undefined' ? null : window) {
    if (isNativeApp()) {
        return nativePermission === 'prompt' || nativePermission === 'prompt-with-rationale';
    }
    return Boolean(win && 'Notification' in win && win.Notification.permission === 'default');
}

/** [알림 켜기]: 권한을 묻고, 앱이면 받자마자 기기 등록 */
export async function requestPush() {
    if (!isNativeApp()) {
        await window.Notification.requestPermission();
        return;
    }
    const { receive } = await PushNotifications.requestPermissions();
    nativePermission = receive;
    if (receive === 'granted') {
        await PushNotifications.register();
    }
}

/**
 * 앱 시작 때 한 번(로그인한 단골). 권한이 이미 있으면 기기를 다시 등록해 토큰을 서버와 맞춥니다.
 * @param {(path: string) => void} navigate  알림을 눌렀을 때 화면 이동
 */
export async function startNativePush(navigate) {
    if (!isNativeApp()) {
        return;
    }
    if (started) {
        // 같은 실행에서 로그아웃 → 다시 로그인: 로그아웃 때 서버에서 지운 이 기기 토큰을 새 계정으로 다시 둡니다
        if (nativePermission === 'granted' && pushEnabled()) {
            await PushNotifications.register();
        }
        return;
    }
    started = true;
    await PushNotifications.addListener('registration', ({ value }) => {
        deviceToken = value;
        api('/me/push-token', { method: 'POST', body: { token: value, platform: Capacitor.getPlatform() } }).catch(
            (error) => console.error('[push] 기기 토큰을 두지 못함', error?.code ?? error?.name)
        );
    });
    await PushNotifications.addListener('registrationError', (error) => {
        // google-services.json 이 없는 빌드(푸시 꺼짐)도 여기로 옵니다
        console.error('[push] 기기 등록 실패', error?.error ?? 'unknown');
    });
    await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
        const path = notification?.data?.path;
        // 서버 고정 문안의 앱 안 경로만(다른 주소로 끌려가지 않게)
        if (typeof path === 'string' && /^\/[a-z/]*$/u.test(path)) {
            navigate(path);
        }
    });
    let { receive } = await PushNotifications.checkPermissions();
    // 「앱 알림」을 꺼 둔 기기는 묻지도 등록하지도 않습니다
    if (!pushEnabled()) {
        nativePermission = receive;
        return;
    }
    /*
     * 앱에 로그인해 들어오면 바로 한 번 묻습니다(사용자 지정 2026-10-16 · D109, 히어로챗과 같음).
     * 예전에는 다음 날 아침 카드 아래에서 처음 물었습니다(시안 05 결정 19). 한 번 거절하면 OS 가 다시 묻지 않으므로
     * 그 자리의 물음(RoomPage)은 아직 안 물은 기기에서만 뜹니다
     */
    if (receive === 'prompt' || receive === 'prompt-with-rationale') {
        ({ receive } = await PushNotifications.requestPermissions());
    }
    nativePermission = receive;
    if (receive === 'granted') {
        await PushNotifications.register();
    }
}

/** 로그아웃 직전: 이 기기 토큰을 서버에서 지움(못 지워도 로그아웃은 됨) */
export async function forgetDeviceToken() {
    if (!isNativeApp() || !deviceToken) {
        return;
    }
    const token = deviceToken;
    deviceToken = null;
    await api('/me/push-token', { method: 'DELETE', body: { token } }).catch(() => {
        // 서버가 못 받아도 다음 로그인 단골에게 토큰 주인이 바뀌어 넘어감
    });
}
